import { GET_CHANNEL_IDEAS_BY_PHASE, Idea } from '@frontend/data-access';

import { useLazyQuery } from '@apollo/client';
import { setChannelPhaseIdeaById } from '@frontend/feature-idea-channel';
import { DroppableStateSnapshot } from '@hello-pangea/dnd';
import { Image, Skeleton } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  selectChannel,
  selectChannelFilters,
  selectIdeaSortby,
  selectLoadingStates,
} from '../../channel-store/selectors';
import {
  useAppDispatch,
  useAppSelector,
} from '../../channel-store/settings/hooks';
import KanbanItem from '../item';
import { CHANNEL_DEFAULT_LIMIT } from '../kanban';
import { StrictModeDroppable as Droppable } from '../strictModeDroppable';

export type ListType = {
  id: string;
  items: Idea[];
  total: number;
};

const KanbanList = ({ id, items, total }: ListType) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectChannel);
  const channelFilters = useAppSelector(selectChannelFilters);
  const sortby = useAppSelector(selectIdeaSortby);
  const phase = channel?.channelPhases.find((c) => c.phase.id === id);
  const loadingStates = useAppSelector(selectLoadingStates);
  const isDataFetching = Object.keys(loadingStates).some(
    (key) => loadingStates[key]
  );

  const { publicRuntimeConfig } = getConfig();
  const router = useRouter();
  const channelId = router.query['id'];

  const [page, setPage] = useState<number>(1);
  const hasMore = useMemo(() => {
    return items.length < total;
  }, [items.length, total]);

  const [getMore, { loading: moreLoading }] = useLazyQuery<{
    channelIdeasByPhase: {
      items: Idea[];
      total: number;
    };
  }>(GET_CHANNEL_IDEAS_BY_PHASE, {
    onCompleted(data) {
      if (phase) {
        dispatch(
          setChannelPhaseIdeaById({
            ...phase,
            ideas: {
              items: [
                ...(phase.ideas?.items || []),
                ...data.channelIdeasByPhase.items,
              ],
              total: data.channelIdeasByPhase.total,
            },
          })
        );
      }
    },
  });

  const refreshFunction = () => {
    if (phase) {
      setChannelPhaseIdeaById({
        ...phase,
        ideas: {
          items: [],
          total: phase.ideas.total,
        },
      });
    }
    setPage(1);
  };

  const getIsDragging = (snapshot: DroppableStateSnapshot) => {
    return (
      snapshot.isDraggingOver &&
      !items.some((i) => i.id === snapshot.draggingFromThisWith)
    );
  };

  const handleLoadMore = () => {
    if (moreLoading) return;
    const totalPages = Math.ceil(total / CHANNEL_DEFAULT_LIMIT); // Calculate total pages
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  useEffect(() => {
    if (moreLoading) return;

    if (channelId && id && page > 1) {
      getMore({
        variables: {
          channelId: channelId,
          phaseId: id,
          page: page,
          limit: CHANNEL_DEFAULT_LIMIT,
          filterBy: {
            ...channelFilters,
          },
          isAsc: sortby === 'ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    setPage(1);
  }, [channelId]);

  if (loadingStates[id]) {
    return (
      <div className="p-2">
        <Skeleton paragraph={{ rows: 3 }} active />
      </div>
    );
  }

  return (
    <Droppable droppableId={id} type="ITEM">
      {(provided, snapshot) => (
        <div
          id={id}
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`infinite-scroll-container rounded-lg h-full overflow-y-auto ${
            getIsDragging(snapshot) &&
            'border-2 border-dashed border-gray-500 bg-gray-300'
          }`}
        >
          {getIsDragging(snapshot) ? (
            <div className="p-6 flex justify-center items-center h-full font-semibold">
              {phase?.phase.title}
            </div>
          ) : (
            <>
              {items.length > 0 ? (
                <InfiniteScroll
                  style={{
                    height: '100%',
                    overflow: 'unset',
                  }}
                  scrollableTarget={id}
                  refreshFunction={refreshFunction}
                  next={handleLoadMore}
                  dataLength={items.length}
                  hasMore={hasMore}
                  loader={
                    <div className="p-2">
                      <Skeleton paragraph={{ rows: 4 }} active />
                    </div>
                  }
                >
                  {items.map((item, index) => (
                    <KanbanItem
                      key={item.id}
                      index={index}
                      data={item}
                      isDataFetching={isDataFetching}
                    />
                  ))}
                </InfiniteScroll>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-sm text-gray-700 font-normal select-none">
                  <Image
                    preview={false}
                    src={`${publicRuntimeConfig.staticFolder}/svg/add-star.svg`}
                  />
                  {t('no_data')}
                </div>
              )}
              {provided.placeholder}
            </>
          )}
        </div>
      )}
    </Droppable>
  );
};

export default KanbanList;
